import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import API from "../api/axiosConfig";
import { toast } from "react-toastify";
import { useNavigate, Link } from "react-router-dom";
import {
  User, Mail, Lock, Building2, FileText, Tag,
  Upload, Eye, EyeOff, ChevronRight, ChevronLeft,
  CheckCircle2, Store,
} from "lucide-react";

function toSlug(str) {
  return str.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9\s-]/g, "").trim().replace(/\s+/g, "-");
}

const ALL_STEPS    = ["Tu cuenta", "Tu comercio", "Categoría y logo"];
const BUSINESS_ONLY_STEPS = ["Tu comercio", "Categoría y logo"];

export default function RegisterBusiness() {
  const navigate = useNavigate();
  // Si ya hay sesión activa no tiene sentido volver a pedir nombre/correo/contraseña —
  // esos datos ya existen, solo falta la info del comercio. En ese caso el paso
  // "Tu cuenta" se salta por completo y se usa /companies/register (autenticado,
  // solo datos del comercio) en vez de /companies/public-register (crea usuario nuevo).
  const isLoggedIn = !!(localStorage.getItem("accessToken") || localStorage.getItem("token"));
  const STEPS = isLoggedIn ? BUSINESS_ONLY_STEPS : ALL_STEPS;
  const ACCOUNT_STEP  = isLoggedIn ? -1 : 0;
  const BUSINESS_STEP = isLoggedIn ? 0  : 1;
  const CATEGORY_STEP = isLoggedIn ? 1  : 2;
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [account, setAccount] = useState({ name: "", email: "", password: "" });
  const [business, setBusiness] = useState({ businessName: "", slug: "", description: "" });
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [categoryId, setCategoryId] = useState("");
  const [categories, setCategories] = useState([]);
  const fileRef = useRef(null);

  useEffect(() => {
    const base = (process.env.REACT_APP_API_URL || "http://localhost:4000/api").replace(/\/$/, "");
    axios.get(`${base}/categories`)
      .then(({ data }) => {
        const raw = data?.data?.categories ?? data?.categories ?? data;
        setCategories(Array.isArray(raw) ? raw : []);
      })
      .catch(() => toast.error("Error cargando categorías"));
  }, []);

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.warn("El logo no debe superar 5 MB"); return; }
    setLogo(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const canAdvance = () => {
    if (step === ACCOUNT_STEP) {
      if (!account.name.trim() || account.name.trim().length < 2) { toast.warn("Ingresa tu nombre completo"); return false; }
      if (!account.email.trim() || !/\S+@\S+\.\S+/.test(account.email)) { toast.warn("Correo inválido"); return false; }
      if (!account.password || account.password.length < 6) { toast.warn("La contraseña debe tener al menos 6 caracteres"); return false; }
      return true;
    }
    if (step === BUSINESS_STEP) {
      if (!business.businessName.trim()) { toast.warn("Ingresa el nombre del comercio"); return false; }
      if (!business.slug.trim()) { toast.warn("El slug no puede estar vacío"); return false; }
      return true;
    }
    return true;
  };

  const nextStep = () => { if (canAdvance()) setStep((s) => s + 1); };
  const prevStep = () => setStep((s) => s - 1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!categoryId) { toast.warn("Selecciona una categoría"); return; }
    setLoading(true);
    try {
      let res;
      if (isLoggedIn) {
        // Ya hay cuenta -- solo se manda la info del comercio, /companies/register
        // convierte la cuenta actual (req.user) en dueño del comercio.
        const fd = new FormData();
        fd.append("name",        business.businessName.trim());
        fd.append("slug",        business.slug.trim());
        fd.append("description", business.description.trim());
        fd.append("category",    categoryId);
        if (logo instanceof File) fd.append("logo", logo);
        res = await API.post("/companies/register", fd);
      } else {
        const fd = new FormData();
        fd.append("userName",     account.name.trim());
        fd.append("email",        account.email.trim());
        fd.append("password",     account.password);
        fd.append("businessName", business.businessName.trim());
        fd.append("slug",         business.slug.trim());
        fd.append("description",  business.description.trim());
        fd.append("category",     categoryId);
        if (logo instanceof File) fd.append("logo", logo);
        res = await API.post("/companies/public-register", fd);
      }
      const data = res.data?.data ?? res.data;

      localStorage.setItem("token",        data.tokens.accessToken);
      localStorage.setItem("accessToken",  data.tokens.accessToken);
      localStorage.setItem("refreshToken", data.tokens.refreshToken ?? "");
      localStorage.setItem("user",         JSON.stringify(data.user));

      toast.success("¡Comercio registrado con éxito! Bienvenido 🎉");
      navigate("/company");
    } catch (err) {
      toast.error(err.response?.data?.msg || err.response?.data?.error || "Error al registrar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-white to-supernova-50 flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-lg">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-supernova-400 rounded-2xl shadow-lg mb-4">
            <Store className="text-black" size={32} />
          </div>
          <h1 className="text-3xl font-bold text-gray-800">Registra tu comercio</h1>
          <p className="text-gray-500 mt-1 text-sm">
            {isLoggedIn ? (
              "Completa los datos de tu comercio para continuar"
            ) : (
              <>
                ¿Ya tienes cuenta?{" "}
                <Link to="/login" className="text-supernova-600 font-semibold hover:underline">Inicia sesión</Link>
              </>
            )}
          </p>
        </div>

        <div className="flex items-center justify-center gap-2 mb-8">
          {STEPS.map((label, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${i === step ? "bg-supernova-400 text-black shadow" : i < step ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-400"}`}>
                {i < step ? <CheckCircle2 size={12} /> : <span>{i + 1}</span>}
                <span className="hidden sm:inline">{label}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`w-6 h-0.5 rounded ${i < step ? "bg-green-400" : "bg-gray-200"}`} />}
            </div>
          ))}
        </div>

        <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
          <form onSubmit={step === CATEGORY_STEP ? handleSubmit : (e) => e.preventDefault()}>
            <AnimatePresence mode="wait">
              {step === ACCOUNT_STEP && (
                <motion.div key="s0" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.25 }} className="p-8 space-y-4">
                  <h2 className="text-xl font-bold text-gray-800">Tu cuenta</h2>
                  <p className="text-gray-500 text-sm">Crea tus credenciales de acceso</p>
                  <Field icon={<User size={16} />} label="Nombre completo">
                    <input type="text" placeholder="Ej: Ana García" value={account.name} onChange={(e) => setAccount({ ...account, name: e.target.value })} className="w-full outline-none text-sm text-gray-800 placeholder-gray-400" />
                  </Field>
                  <Field icon={<Mail size={16} />} label="Correo electrónico">
                    <input type="email" placeholder="correo@empresa.com" value={account.email} onChange={(e) => setAccount({ ...account, email: e.target.value })} className="w-full outline-none text-sm text-gray-800 placeholder-gray-400" />
                  </Field>
                  <Field icon={<Lock size={16} />} label="Contraseña" extra={<button type="button" onClick={() => setShowPass(!showPass)} className="text-gray-400 hover:text-gray-600">{showPass ? <EyeOff size={15} /> : <Eye size={15} />}</button>}>
                    <input type={showPass ? "text" : "password"} placeholder="Mínimo 6 caracteres" value={account.password} onChange={(e) => setAccount({ ...account, password: e.target.value })} className="w-full outline-none text-sm text-gray-800 placeholder-gray-400" />
                  </Field>
                </motion.div>
              )}

              {step === BUSINESS_STEP && (
                <motion.div key="s1" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.25 }} className="p-8 space-y-4">
                  <h2 className="text-xl font-bold text-gray-800">Tu comercio</h2>
                  <p className="text-gray-500 text-sm">Información pública de tu negocio</p>
                  <Field icon={<Building2 size={16} />} label="Nombre del comercio">
                    <input type="text" placeholder="Ej: Tienda La Esperanza" value={business.businessName} onChange={(e) => setBusiness({ ...business, businessName: e.target.value, slug: toSlug(e.target.value) })} className="w-full outline-none text-sm text-gray-800 placeholder-gray-400" />
                  </Field>
                  <div>
                    <label className="text-xs text-gray-500 font-medium mb-1 block">URL de tu comercio</label>
                    <div className="flex items-center gap-1 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5">
                      <span className="text-gray-400 text-xs whitespace-nowrap">qkiere.com/</span>
                      <input type="text" value={business.slug} onChange={(e) => setBusiness({ ...business, slug: toSlug(e.target.value) })} className="w-full outline-none text-sm text-supernova-600 font-medium bg-transparent" placeholder="mi-comercio" />
                    </div>
                  </div>
                  <Field icon={<FileText size={16} />} label="Descripción (opcional)">
                    <textarea rows={3} placeholder="Describe brevemente tu negocio..." value={business.description} onChange={(e) => setBusiness({ ...business, description: e.target.value })} className="w-full outline-none text-sm text-gray-800 placeholder-gray-400 resize-none" />
                  </Field>
                </motion.div>
              )}

              {step === CATEGORY_STEP && (
                <motion.div key="s2" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.25 }} className="p-8 space-y-6">
                  <div>
                    <h2 className="text-xl font-bold text-gray-800 mb-1">Categoría y logo</h2>
                    <p className="text-gray-500 text-sm mb-4">A qué sector pertenece tu negocio</p>
                    <div className="flex items-center gap-1.5 mb-3">
                      <Tag size={14} className="text-gray-400" />
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Categoría</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-52 overflow-y-auto pr-1">
                      {categories.map((cat) => (
                        <button key={cat.id} type="button" onClick={() => setCategoryId(cat.id)}
                          className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-medium transition-all text-left ${categoryId === cat.id ? "border-supernova-400 bg-supernova-50 text-gray-800 shadow-sm" : "border-gray-200 text-gray-600 hover:border-supernova-300 hover:bg-supernova-50/50"}`}>
                          {cat.icon && <span className="text-base">{cat.icon}</span>}
                          <span className="truncate leading-tight">{cat.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5 mb-3">
                      <Upload size={14} className="text-gray-400" />
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Logo</span>
                      <span className="text-xs text-gray-400 ml-auto">(opcional)</span>
                    </div>
                    <input type="file" accept="image/*" ref={fileRef} className="hidden" onChange={handleLogoChange} />
                    {logoPreview ? (
                      <div className="flex items-center gap-4">
                        <img src={logoPreview} alt="preview" className="w-20 h-20 rounded-2xl object-cover border-2 border-supernova-300 shadow" />
                        <div>
                          <p className="text-sm font-semibold text-gray-700 truncate max-w-xs">{logo?.name}</p>
                          <button type="button" onClick={() => fileRef.current?.click()} className="mt-2 text-xs text-supernova-600 font-semibold hover:underline">Cambiar imagen</button>
                        </div>
                      </div>
                    ) : (
                      <button type="button" onClick={() => fileRef.current?.click()} className="w-full border-2 border-dashed border-gray-200 rounded-2xl py-8 flex flex-col items-center gap-2 text-gray-400 hover:border-supernova-300 hover:text-supernova-500 transition-colors">
                        <Upload size={28} />
                        <span className="text-sm font-medium">Haz clic para subir tu logo</span>
                        <span className="text-xs">PNG, JPG o WEBP · máx. 5 MB</span>
                      </button>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="px-8 pb-8 flex gap-3">
              {step > 0 && (
                <button type="button" onClick={prevStep} className="flex items-center gap-1.5 px-4 py-3 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 font-medium text-sm transition">
                  <ChevronLeft size={16} /> Atrás
                </button>
              )}
              {step < CATEGORY_STEP ? (
                <button type="button" onClick={nextStep} className="flex-1 flex items-center justify-center gap-2 bg-supernova-400 hover:bg-supernova-500 text-black font-bold py-3 rounded-xl transition shadow-sm">
                  Siguiente <ChevronRight size={16} />
                </button>
              ) : (
                <button type="submit" disabled={loading} className="flex-1 flex items-center justify-center gap-2 bg-supernova-400 hover:bg-supernova-500 disabled:opacity-60 text-black font-bold py-3 rounded-xl transition shadow-sm">
                  {loading ? <><span className="animate-spin w-4 h-4 border-2 border-black border-t-transparent rounded-full inline-block" /> Registrando...</> : <><CheckCircle2 size={18} /> Registrar comercio</>}
                </button>
              )}
            </div>
          </form>
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">
          Al registrarte aceptas los <span className="underline cursor-pointer">términos y condiciones</span> de Qkiere.com
        </p>
      </motion.div>
    </div>
  );
}

function Field({ icon, label, children, extra }) {
  return (
    <div>
      <label className="text-xs text-gray-500 font-medium mb-1 block">{label}</label>
      <div className="flex items-center gap-2.5 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 focus-within:border-supernova-400 focus-within:bg-white transition">
        <span className="text-gray-400 flex-shrink-0">{icon}</span>
        {children}
        {extra && <span className="flex-shrink-0">{extra}</span>}
      </div>
    </div>
  );
}
